import type { Post } from "../types";

const API_BASE: string = (process.env.REACT_APP_API_BASE || "http://localhost:5000").replace(/\/$/, "");

function authHeaders(): Record<string, string> {
  const token = localStorage.getItem("token");
  return {
    "Content-Type": "application/json",
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

export type SavedLibraryType = "all" | "posts" | "spaces" | "people" | "notes";
export type SavedLibrarySort = "saved-newest" | "item-newest" | "most-loved";
export type SavedLibraryKind = "post" | "space" | "person" | "note";

export type SavedLibraryItem = {
  kind: SavedLibraryKind;
  id: string;
  savedAt?: string | null;
  sortDate?: string | null;
  title: string;
  subtitle?: string;
  content?: string;
  href?: string;
  accentColor?: string;
  accessMode?: string;
  members?: string;
  username?: string;
  displayName?: string;
  avatarUrl?: string;
  color?: string;
  tags?: string[];
  type?: string;
  post?: Post;
};

export type SavedLibraryResponse = {
  success: boolean;
  items: SavedLibraryItem[];
  totals: Record<SavedLibraryType, number>;
  page: number;
  limit: number;
  total: number;
  hasMore: boolean;
  nextPage: number | null;
};

export type FetchSavedLibraryParams = {
  page?: number;
  limit?: number;
  q?: string;
  type?: SavedLibraryType;
  sort?: SavedLibrarySort;
  signal?: AbortSignal;
};

export async function fetchSavedLibrary(params: FetchSavedLibraryParams = {}): Promise<SavedLibraryResponse> {
  const url = new URL(`${API_BASE}/api/saved`);
  url.searchParams.set("page", String(params.page || 1));
  url.searchParams.set("limit", String(params.limit || 12));
  url.searchParams.set("type", params.type || "all");
  url.searchParams.set("sort", params.sort || "saved-newest");
  if (params.q?.trim()) url.searchParams.set("q", params.q.trim());

  const res = await fetch(url.toString(), {
    headers: authHeaders(),
    signal: params.signal,
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || `Saved failed: ${res.status}`);
  }

  return {
    success: Boolean(data.success),
    items: data.items || [],
    totals: {
      all: data.totals?.all || 0,
      posts: data.totals?.posts || 0,
      spaces: data.totals?.spaces || 0,
      people: data.totals?.people || 0,
      notes: data.totals?.notes || 0,
    },
    page: data.page || 1,
    limit: data.limit || params.limit || 12,
    total: data.total || data.totals?.all || 0,
    hasMore: Boolean(data.hasMore),
    nextPage: data.nextPage || null,
  };
}

export async function removeSavedLibraryItem(kind: SavedLibraryKind, id: string): Promise<void> {
  if (kind === "note") {
    const res = await fetch(`${API_BASE}/api/notes/${encodeURIComponent(id)}/trash`, {
      method: "PATCH",
      headers: authHeaders(),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) {
      throw new Error(data?.message || `Remove saved note failed: ${res.status}`);
    }
    return;
  }

  const backendKind = kind === "person" ? "user" : kind;
  const res = await fetch(`${API_BASE}/api/saved/${encodeURIComponent(backendKind)}/${encodeURIComponent(id)}`, {
    method: "DELETE",
    headers: authHeaders(),
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw new Error(data?.message || `Remove saved failed: ${res.status}`);
  }
}
